import { Events, GridApi } from '@ag-grid-community/core'
import { Observable, Subscription } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { COMMENTARY_COLUMN_ID } from '../columns/get-commentary-column'
import { CommentaryLiveAccountsService } from '../services/commentary-live-accounts.service'

export interface CommentaryCountsWiring<TRow> {
  changes$: Observable<void>
  getApi: () => GridApi
  hasAccessForLoad: () => boolean
  /**
   * The component's own Subject, one per grid — a `pipe(...)` or
   * `asObservable()` is a fresh object per call and silently defeats the keying
   * below, and two grids sharing one Subject re-point each other's listener.
   */
  destroy$: Observable<void>
  accountIdOf: (row: TRow | undefined) => string | number | null | undefined
  loadCounts: (accountIds: (string | number)[]) => void
  /**
   * DLITE-5094: register the grid's loaded accounts for live updates. The
   * owner is `destroy$` itself — the same identity this wiring keys on — and it
   * is released when `destroy$` fires. `entity` for per-item grids, `total`
   * for per-account-total grids.
   */
  live?: {
    accounts: Pick<CommentaryLiveAccountsService, 'setOwner' | 'releaseOwner'>
    surface: 'entity' | 'total'
  }
}

interface WiredGrid {
  api: GridApi | null
  handler: () => void
  load: () => void
  refresh: () => void
  changes$: Observable<void>
  refreshSub: Subscription
  live: CommentaryCountsWiring<unknown>['live']
}

const wired = new WeakMap<Observable<void>, WiredGrid>()

function loadFrom<TRow>(w: CommentaryCountsWiring<TRow>): () => void {
  return () => {
    if (!w.hasAccessForLoad()) return
    const ids: (string | number)[] = []
    w.getApi().forEachNode(node => {
      const id = w.accountIdOf(node.data)
      if (id != null) ids.push(id)
    })
    w.loadCounts(ids)
    w.live?.accounts.setOwner(w.destroy$, w.live.surface, ids)
  }
}

function refreshFrom<TRow>(w: CommentaryCountsWiring<TRow>): () => void {
  return () => {
    const api = w.getApi()
    // The value cache is on, so refreshing without expiring first repaints from
    // the stale cached getter result.
    api.expireValueCache()
    api.refreshCells({ columns: [COMMENTARY_COLUMN_ID], force: true })
  }
}

function subscribeRefresh<TRow>(
  w: CommentaryCountsWiring<TRow>,
  grid: WiredGrid,
): Subscription {
  return w.changes$.pipe(takeUntil(w.destroy$)).subscribe(() => grid.refresh())
}

export function wireCommentaryCounts<TRow>(
  w: CommentaryCountsWiring<TRow>,
): void {
  const api = w.getApi()
  const existing = wired.get(w.destroy$)

  if (existing != null) {
    // A re-wire supersedes the previous call's wiring. Without this the listener
    // moves to the new api while the handler and the refresh keep reading the
    // first call's callbacks and the first call's stream.
    existing.load = loadFrom(w)
    existing.refresh = refreshFrom(w)
    if (existing.live && existing.live.accounts !== w.live?.accounts) {
      existing.live.accounts.releaseOwner(w.destroy$)
    }
    existing.live = w.live
    if (existing.changes$ !== w.changes$) {
      existing.refreshSub.unsubscribe()
      existing.changes$ = w.changes$
      existing.refreshSub = subscribeRefresh(w, existing)
    }
    if (existing.api !== api) {
      existing.api?.removeEventListener?.(
        Events.EVENT_MODEL_UPDATED,
        existing.handler,
      )
      api.addEventListener(Events.EVENT_MODEL_UPDATED, existing.handler)
      existing.api = api
    }
    return
  }

  let torndown = false
  const grid: WiredGrid = {
    api: null,
    load: loadFrom(w),
    refresh: refreshFrom(w),
    handler: () => grid.load(),
    changes$: w.changes$,
    refreshSub: Subscription.EMPTY,
    live: w.live,
  }

  const teardown = () => {
    if (torndown) return
    torndown = true
    grid.api?.removeEventListener?.(Events.EVENT_MODEL_UPDATED, grid.handler)
    // DLITE-5094: the grid no longer displays anything.
    grid.live?.accounts.releaseOwner(w.destroy$)
    // Not merely nulled: a surviving record sends the next call for this
    // destroy$ into the re-point branch, which re-registers the handler.
    wired.delete(w.destroy$)
  }

  w.destroy$.pipe(take(1)).subscribe({
    next: () => teardown(),
    complete: () => teardown(),
  })
  // An already-completed destroy$ delivered `complete` synchronously above, so
  // teardown has run and anything registered past here is unremovable.
  if (torndown) return

  grid.refreshSub = subscribeRefresh(w, grid)

  // A replaying changes$ ran the callback synchronously, so teardown may have
  // fired since the check above.
  if (torndown) return

  grid.api = api
  api.addEventListener(Events.EVENT_MODEL_UPDATED, grid.handler)
  wired.set(w.destroy$, grid)
}
